import { useEffect, useState } from "react";
import { approveContract, createContract, listContracts } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Contract {
  id: string;
  contractNumber: string;
  clientId: string;
  title: string;
  value: number;
  status: string;
  startDate: string | null;
  endDate: string | null;
  signedByClient: string | null;
  currency: string;
  version: number;
}

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "text-gray-400 bg-gray-800",
  PENDING_APPROVAL: "text-yellow-400 bg-yellow-900/30",
  ACTIVE: "text-green-400 bg-green-900/30",
  EXPIRED: "text-gray-500 bg-gray-800",
  TERMINATED: "text-red-400 bg-red-900/30",
};

function currency(n: number, c = "INR") {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: c,
    maximumFractionDigits: 0,
  }).format(n);
}

export function FinanceContractsPage() {
  const { token } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    clientId: "",
    title: "",
    value: "",
    content: "",
    startDate: "",
    endDate: "",
  });
  const [creating, setCreating] = useState(false);

  const reload = () => {
    void listContracts(token).then((d) => {
      setContracts((d as { items: Contract[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!form.clientId || !form.title || !form.value) return;
    setCreating(true);
    await createContract(token, {
      clientId: form.clientId,
      title: form.title,
      content: form.content || `Contract for ${form.title}`,
      value: parseFloat(form.value),
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
    });
    setShowForm(false);
    setCreating(false);
    setForm({ clientId: "", title: "", value: "", content: "", startDate: "", endDate: "" });
    reload();
  };

  const approve = async (c: Contract) => {
    if (!window.confirm("Approve this contract? This is a legally binding action.")) return;
    await approveContract(token, c.id, c.version);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Contracts</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
          >
            {showForm ? "Cancel" : "New Contract"}
          </button>
        </div>
        <div className="mb-6 rounded-lg border border-yellow-800 bg-yellow-950/30 p-3 text-sm text-yellow-300">
          Human approval required before any contract becomes active. AI may draft only.
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                placeholder="Client ID"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Contract Title"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                placeholder="Contract Value (₹)"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                type="date"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
              />
              <input
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                type="date"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
              />
            </div>
            <textarea
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              placeholder="Contract content / terms..."
              rows={4}
              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
            />
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Contract"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {contracts.length === 0 && <p className="text-gray-400">No contracts yet.</p>}
            {contracts.map((c) => (
              <div key={c.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{c.contractNumber}</span>
                    <span className="ml-3 font-medium text-white">{c.title}</span>
                    {c.signedByClient && (
                      <span className="ml-2 text-xs text-green-400">Signed</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-white">
                      {currency(c.value, c.currency)}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[c.status] ?? "bg-gray-800 text-gray-400"}`}
                    >
                      {c.status}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex gap-3 text-xs text-gray-500">
                  {c.startDate && <span>Start: {new Date(c.startDate).toLocaleDateString()}</span>}
                  {c.endDate && <span>End: {new Date(c.endDate).toLocaleDateString()}</span>}
                  {c.status === "DRAFT" && (
                    <button
                      onClick={() => void approve(c)}
                      className="rounded bg-blue-900/50 px-2 py-0.5 text-blue-300 hover:bg-blue-900"
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
